// BeneAIConversationDropdown — drawer header recents picker.
//
// v1 parity. Replaces the route-context badge ("settings", "commitment")
// that previously sat next to "BeneAI" with a click-target showing the
// current conversation's title and an arrow that opens a list of recent
// conversations. Selecting one swaps the drawer over to it. Backend
// already keeps the list ordered most-recent-first per user via
// ``GET /conversations``; titles arrive populated for older sessions
// once the Haiku auto-titler runs.

import { ChevronDown, X } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";

import { deleteConversation, listConversations } from "./api";
import { useBeneAIDrawer } from "./BeneAIDrawerContext";
import type { BeneAIConversation } from "./types";


const NEW_LABEL = "New conversation";


function labelFor(row: BeneAIConversation): string {
  // The backend ships ``title`` for sessions the auto-titler has
  // touched, ``preview`` otherwise. Both can be empty for a brand-new
  // session that hasn't accumulated a turn yet.
  if (row.title) return row.title;
  if (row.preview) return row.preview;
  return "Untitled chat";
}


export function BeneAIConversationDropdown() {
  const drawer = useBeneAIDrawer();
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState<BeneAIConversation[] | null>(null);
  const [loading, setLoading] = useState(false);
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  // Prefetch the list so the trigger can show the current
  // conversation's title without waiting for a click. Re-runs when
  // the drawer switches conversations (the new id might not be in
  // the prior rows snapshot — e.g. a fresh chat just minted by the
  // backend on the first send).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await listConversations();
        // Defensive: tolerate an unexpected/empty payload rather than crashing
        // the dropdown on `undefined.find` (prod always returns {rows, total}).
        if (!cancelled) setRows(data?.rows ?? []);
      } catch {
        // Leave rows as-is; the trigger falls through to its
        // generic "Conversation" label.
      }
    })();
    return () => { cancelled = true; };
  }, [drawer.conversationId]);

  // Click-outside closes the popover. The drawer's overlay catches
  // clicks on the page but not within the drawer itself, so we still
  // need this handler to dismiss the list when the user clicks
  // elsewhere inside the drawer (e.g. into the composer).
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      const wrapper = wrapperRef.current;
      if (wrapper === null) return;
      if (!wrapper.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const toggleOpen = useCallback(async () => {
    const next = !open;
    setOpen(next);
    if (!next) return;
    setLoading(true);
    try {
      const data = await listConversations();
      setRows(data.rows);
    } catch {
      // Network/auth errors leave the list empty; the user sees "no
      // recent conversations" until they retry.
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [open]);

  const handlePick = useCallback(
    async (conversationId: string) => {
      setOpen(false);
      if (conversationId === drawer.conversationId) return;
      try {
        await drawer.loadConversation(conversationId);
      } catch {
        // Surface failures via a silent no-op for now; the drawer's
        // own send path will report the error if the user tries to
        // interact further with a broken state.
      }
    },
    [drawer],
  );

  const handleDelete = useCallback(
    async (conversationId: string) => {
      // Optimistically drop the row from the list so the UX feels
      // immediate; the backend call follows. If it fails we refetch
      // to put the row back.
      setRows((prev) =>
        prev === null
          ? prev
          : prev.filter((r) => r.conversation_id !== conversationId),
      );
      // If the user just deleted the conversation they were viewing,
      // reset the drawer so they don't keep posting into an
      // ENDED session.
      if (conversationId === drawer.conversationId) {
        drawer.reset();
      }
      try {
        await deleteConversation(conversationId);
      } catch {
        try {
          const data = await listConversations();
          setRows(data.rows);
        } catch {
          // Final fallback: leave the optimistic state in place.
        }
      }
    },
    [drawer],
  );

  const currentLabel = (() => {
    if (drawer.conversationId === null) return NEW_LABEL;
    if (rows !== null) {
      const match = rows.find(
        (r) => r.conversation_id === drawer.conversationId,
      );
      if (match) return labelFor(match);
    }
    return "Conversation";
  })();

  return (
    <div ref={wrapperRef} className="relative">
      <button
        type="button"
        data-testid="beneai-conversation-dropdown-toggle"
        onClick={() => { void toggleOpen(); }}
        className="flex max-w-[180px] items-center gap-1 rounded text-xs text-text-tertiary transition-fast ease-out hover:text-text-primary"
      >
        <span className="truncate">{currentLabel}</span>
        <ChevronDown size={12} aria-hidden="true" />
      </button>
      {open && (
        <div
          data-testid="beneai-conversation-dropdown-list"
          className="absolute left-0 top-full z-10 mt-1 w-72 overflow-hidden rounded-md border border-border-subtle bg-canvas shadow-lg"
        >
          {loading && rows === null && (
            <div className="px-3 py-2 text-xs text-text-tertiary">
              Loading…
            </div>
          )}
          {rows !== null && rows.length === 0 && (
            <div className="px-3 py-2 text-xs text-text-tertiary">
              No recent conversations.
            </div>
          )}
          {rows !== null && rows.length > 0 && (
            <ul className="max-h-72 overflow-y-auto py-1">
              {rows.map((row) => (
                <li key={row.conversation_id} className="group flex items-stretch">
                  <button
                    type="button"
                    onClick={() => { void handlePick(row.conversation_id); }}
                    className={
                      "flex-1 truncate px-3 py-2 text-left text-xs "
                      + "transition-fast ease-out hover:bg-recessed "
                      + (row.conversation_id === drawer.conversationId
                        ? "bg-recessed font-semibold text-text-primary"
                        : "text-text-secondary")
                    }
                    title={labelFor(row)}
                  >
                    {labelFor(row)}
                  </button>
                  <button
                    type="button"
                    aria-label="Delete conversation"
                    data-testid="beneai-conversation-dropdown-delete"
                    onClick={(e) => {
                      e.stopPropagation();
                      void handleDelete(row.conversation_id);
                    }}
                    className="flex w-7 items-center justify-center text-text-tertiary opacity-0 transition-fast ease-out hover:text-error focus:opacity-100 group-hover:opacity-100"
                    title="Delete this conversation"
                  >
                    <X size={12} aria-hidden="true" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
